import { useState } from 'react';
import type { Template, Software, SharedDisk } from '../types';
import { mockSoftware } from '../mockData';
import SnapshotModal from './SnapshotModal';

interface TemplateDetailModalProps {
  template: Template;
  onClose: () => void;
  onEdit: (template: Template) => void;
  onDelete: (template: Template) => void;
}

export default function TemplateDetailModal({ template, onClose, onEdit, onDelete }: TemplateDetailModalProps) {
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [showSoftwareModal, setShowSoftwareModal] = useState(false);
  const [showDiskModal, setShowDiskModal] = useState(false);
  const [softwareSearch, setSoftwareSearch] = useState('');
  const [softwareCategory, setSoftwareCategory] = useState('all');

  const categories = Array.from(new Set(mockSoftware.map(s => s.category)));

  const filteredSoftware = mockSoftware.filter(sw => {
    if (softwareSearch && !sw.name.toLowerCase().includes(softwareSearch.toLowerCase())) return false;
    if (softwareCategory !== 'all' && sw.category !== softwareCategory) return false;
    return !template.preInstalledSoftware.some(s => s.id === sw.id);
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">模板详情</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors whitespace-nowrap">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">模板名称</label>
                <div className="text-sm text-gray-900">{template.name}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">模板ID</label>
                <div className="text-sm text-gray-900">{template.id}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">状态</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                  template.status === 'published' ? 'bg-green-100 text-green-800' :
                  template.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                  'bg-orange-100 text-orange-800'
                }`}>
                  {template.status === 'published' ? '已上架' : template.status === 'draft' ? '草稿' : '已下架'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">当前版本</label>
                <div className="text-sm text-gray-900">{template.currentVersion}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">操作系统</label>
                <div className="text-sm text-gray-900">{template.os}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">上一版本</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-900">{template.previousVersion || '-'}</span>
                  {template.snapshots && template.snapshots.length > 0 && (
                    <button
                      onClick={() => setShowSnapshotModal(true)}
                      className="text-xs text-teal-600 hover:text-teal-700 whitespace-nowrap"
                    >
                      查看快照记录
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">模板用途</label>
                <div className="text-sm text-gray-900">{template.purpose}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">基础镜像</label>
                <div className="text-sm text-gray-900">{template.baseImage}</div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">预装软件列表</label>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 whitespace-nowrap">应用名称</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 whitespace-nowrap">应用版本</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 whitespace-nowrap">保存位置</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 whitespace-nowrap">软件简述</th>
                    </tr>
                  </thead>
                  <tbody>
                    {template.preInstalledSoftware.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-sm text-gray-400">
                          暂无预装软件
                        </td>
                      </tr>
                    ) : (
                      template.preInstalledSoftware.map((sw) => (
                        <tr key={sw.id} className="border-t border-gray-100">
                          <td className="py-2 px-3 text-sm text-gray-900 whitespace-nowrap">{sw.name}</td>
                          <td className="py-2 px-3 text-sm text-gray-600 whitespace-nowrap">{sw.version}</td>
                          <td className="py-2 px-3 text-sm text-gray-600 whitespace-nowrap">{sw.savePath}</td>
                          <td className="py-2 px-3 text-sm text-gray-600 whitespace-nowrap">{sw.description}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">最低配置</h4>
              <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">CPU核心数</label>
                  <div className="text-sm font-medium text-gray-900">{template.minConfig.cpuCores} 核</div>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">内存容量</label>
                  <div className="text-sm font-medium text-gray-900">{template.minConfig.memory} GB</div>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">系统盘容量</label>
                  <div className="text-sm font-medium text-gray-900">{template.minConfig.systemDisk} GB</div>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">必须物理机</label>
                  <div className="text-sm font-medium text-gray-900">{template.minConfig.requirePhysical ? '是' : '否'}</div>
                </div>
                {template.minConfig.gpu && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">GPU卡数量</label>
                      <div className="text-sm font-medium text-gray-900">{template.minConfig.gpu.count} 张</div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">可选型号</label>
                      <div className="text-sm font-medium text-gray-900">{template.minConfig.gpu.models.join(', ')}</div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">共享数据盘列表</label>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 whitespace-nowrap">云盘ID</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 whitespace-nowrap">云盘名称</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 whitespace-nowrap">挂载点</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 whitespace-nowrap">云盘空间</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 whitespace-nowrap">云盘归属</th>
                    </tr>
                  </thead>
                  <tbody>
                    {template.sharedDisks.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-sm text-gray-400">
                          暂无共享数据盘
                        </td>
                      </tr>
                    ) : (
                      template.sharedDisks.map((disk) => (
                        <tr key={disk.id} className="border-t border-gray-100">
                          <td className="py-2 px-3 text-sm text-gray-900 whitespace-nowrap">{disk.id}</td>
                          <td className="py-2 px-3 text-sm text-gray-900 whitespace-nowrap">{disk.name}</td>
                          <td className="py-2 px-3 text-sm text-gray-600 whitespace-nowrap">{disk.mountPoint}</td>
                          <td className="py-2 px-3 text-sm text-gray-600 whitespace-nowrap">{disk.space} GB</td>
                          <td className="py-2 px-3 text-sm text-gray-600 whitespace-nowrap">{disk.owner}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onDelete(template)}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors whitespace-nowrap"
            >
              删除
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap">
              关闭
            </button>
            <button
              onClick={() => onEdit(template)}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              编辑
            </button>
          </div>
        </div>
      </div>

      {showSnapshotModal && template.snapshots && (
        <SnapshotModal
          snapshots={template.snapshots}
          onClose={() => setShowSnapshotModal(false)}
        />
      )}
    </div>
  );
}
